import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';
import { RECEIPT_CHANNELS } from './receipt.schemas';

/**
 * The company's own accounts customers pay into (gitaumoses4/chukua#446, part of #146): a bank account, an M-Pesa paybill or an M-Pesa till.
 * The `company_accounts` kind CHECK hard-codes these three, so a new kind is a migration as well as a branch of the input schema below.
 */
export const COMPANY_ACCOUNT_KINDS = ['bank', 'mpesa_paybill', 'mpesa_till'] as const;
export type CompanyAccountKind = (typeof COMPANY_ACCOUNT_KINDS)[number];

export const COMPANY_ACCOUNT_KIND_LABELS: Record<CompanyAccountKind, string> = {
  bank: 'Bank account',
  mpesa_paybill: 'M-Pesa paybill',
  mpesa_till: 'M-Pesa till',
};

/** Active until retired. Retiring is one way: the details stay on the receipts that used them, and a new row is made if they are wanted again. */
export const COMPANY_ACCOUNT_STATUSES = ['active', 'retired'] as const;
export type CompanyAccountStatus = (typeof COMPANY_ACCOUNT_STATUSES)[number];

export const COMPANY_ACCOUNT_STATUS_LABELS: Record<CompanyAccountStatus, string> = {
  active: 'Active',
  retired: 'Retired',
};

const requiredText = (max: number) => z.string().trim().min(1).max(max);

/** Safaricom paybill and till numbers are five to seven digits. */
const mpesaNumber = z
  .string()
  .trim()
  .regex(/^\d{5,7}$/, 'Use the 5 to 7 digit number');

/** `name` is what a receipt shows and keeps, so it is what the picker lists; the rest identifies the account for the channel. */
const bankAccountInputSchema = z
  .object({
    kind: z.literal('bank'),
    name: requiredText(100),
    bankName: requiredText(100),
    accountNumber: requiredText(40),
  })
  .strict();
const paybillInputSchema = z.object({ kind: z.literal('mpesa_paybill'), name: requiredText(100), paybillNumber: mpesaNumber }).strict();
const tillInputSchema = z.object({ kind: z.literal('mpesa_till'), name: requiredText(100), tillNumber: mpesaNumber }).strict();

/** `POST /company-accounts` and `PATCH /company-accounts/:id`; an edit replaces the whole account, so it may change the kind too. */
export const companyAccountInputSchema = z.discriminatedUnion('kind', [bankAccountInputSchema, paybillInputSchema, tillInputSchema]);
export type CompanyAccountInput = z.infer<typeof companyAccountInputSchema>;

export interface CompanyAccountView {
  id: string;
  kind: CompanyAccountKind;
  name: string;
  bankName: string | null;
  accountNumber: string | null;
  paybillNumber: string | null;
  tillNumber: string | null;
  /** The identifying numbers in one line, for a table cell or a picker row. */
  detail: string;
  status: CompanyAccountStatus;
  /** Whether a receipt points at it. A used account can only be retired. */
  isUsed: boolean;
  retiredAt: string | null;
  createdAt: string;
}

/** What the receipt form's picker needs, with no more than `billing.view` to read it. */
export interface CompanyAccountOption {
  id: string;
  kind: CompanyAccountKind;
  name: string;
  detail: string;
  isRetired: boolean;
}

export const companyAccountListQuerySchema = defineListQuery({
  sortFields: ['name', 'kind', 'createdAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    kind: listFilter(z.enum(COMPANY_ACCOUNT_KINDS)),
    status: listFilter(z.enum(COMPANY_ACCOUNT_STATUSES)),
  },
});
export type CompanyAccountListQuery = z.infer<typeof companyAccountListQuerySchema>;

/**
 * `GET /company-accounts/options?channel=`. The picker takes the default (active only); the receipts filter passes `includeRetired=true`,
 * because a retired account is exactly one that holds receipts. Not `z.coerce.boolean()`, which reads "false" as true.
 */
export const companyAccountOptionsQuerySchema = z.object({ channel: z.enum(RECEIPT_CHANNELS), includeRetired: z.enum(['true', 'false']).optional() }).strict();
export type CompanyAccountOptionsQuery = z.infer<typeof companyAccountOptionsQuerySchema>;
